import { useCallback, useEffect, useState, useMemo } from 'react';
import '../Analitos.scss';
import Popup from 'devextreme-react/popup';
import { useApiConfiguration } from '../../../api/api.configuration';
import { ResultTypeResult } from '../../../types/types.analitos';
import { useScreenSize } from '../../../utils/media-query';
import { ReferencesValues } from '../Components/ReferencesValues';
import { ReferencesText } from '../Components/ReferencesText';
import { TemplateConfigurationImproved } from '../Components/TemplateConfigurationImproved';
import { MicrobiologyConfiguration } from '../Components/MicrobiologyConfiguration';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Tabs from 'devextreme-react/tabs';
import { ValidationRules } from '../Components/ValidationRules';
import { ResultListByAnalytes } from '../Components/ResultListByAnalytes';

export const ModalResultTypes = ({ visible, setVisible, TipoResultado, IdAnalito, Analito, ValRefText, configPlantillas }: { visible: boolean; setVisible: (value: boolean) => void; TipoResultado: ResultTypeResult; IdAnalito: number; Analito: string; ValRefText: any; configPlantillas: any }) => {
  const [data, setData] = useState({});
  const [EditItem, setEditItem] = useState<boolean>(true);
  const { getReferencesValues, setAnalitoTemplate } = useApiConfiguration();
  const { isMedium, isSmall, isXSmall } = useScreenSize();

  // Filtra las pestañas según el tipo de resultado
  const Pestanas = useMemo(() => {
    if (TipoResultado === ResultTypeResult.NUMERICO || TipoResultado === ResultTypeResult.FORMULA) {
      return [
        { id: 1, text: 'VALORES DE REFERENCIA' },
        { id: 2, text: 'REGLAS VALIDACIÓN' },
      ];
    }
    if (TipoResultado === ResultTypeResult.LISTA || TipoResultado === ResultTypeResult.AUTOCOMPLETAR) {
      return [
        { id: 0, text: 'RESULTADOS TIPO LISTA' },
        { id: 1, text: 'VALORES DE REFERENCIA' },
        { id: 2, text: 'REGLAS VALIDACIÓN' },
      ];
    }
    if (TipoResultado === ResultTypeResult.PLANTILLA || TipoResultado === ResultTypeResult.IMAGENESDX) {
      return [{ id: 3, text: 'PLANTILLAS' }];
    }
    if (TipoResultado === ResultTypeResult.MICROBIOLOGIA) {
      return [{ id: 4, text: 'CONFIGURACIÓN MICROBIOLOGÍA' }];
    }
    return [
      { id: 1, text: 'VALORES DE REFERENCIA' },
      { id: 2, text: 'REGLAS VALIDACIÓN' },
    ];
  }, [TipoResultado]);

  // Establecer la primera pestaña como activa
  const [selectedTab, setSelectedTab] = useState(() => (Pestanas.length > 0 ? Pestanas[0].id : 0));

  useEffect(() => {
    if (Pestanas.length > 0) {
      setSelectedTab(Pestanas[0].id);
    }
  }, [Pestanas]);

  useEffect(() => {
    if (TipoResultado === ResultTypeResult.NUMERICO || TipoResultado === ResultTypeResult.FORMULA) {
      getReferencesValues(IdAnalito).then((res) => {
        setData(res);
      });
    }
    if (TipoResultado === ResultTypeResult.TEXTUAL) {
      setEditItem(true);
    }
  }, [IdAnalito]);

  const handleVisible = useCallback(() => {
    setVisible(false);
  }, []);

  const handleTabClick = (e) => {
    setSelectedTab(e.itemData.id);
  };

  // Determinar si mostrar el indicador de rangos extendidos
  const shouldShowRangosExtendidos = TipoResultado === ResultTypeResult.NUMERICO || TipoResultado === ResultTypeResult.FORMULA;

  // Título con indicador de tipo de resultado
  const getModalTitle = () => {
    const tipoTexto = TipoResultado === ResultTypeResult.NUMERICO ? 'NUMÉRICO' : TipoResultado === ResultTypeResult.FORMULA ? 'FÓRMULA' : TipoResultado === ResultTypeResult.LISTA ? 'LISTA' : TipoResultado === ResultTypeResult.AUTOCOMPLETAR ? 'AUTOCOMPLETAR' : TipoResultado === ResultTypeResult.PLANTILLA ? 'PLANTILLA' : TipoResultado === ResultTypeResult.TEXTUAL ? 'TEXTUAL' : TipoResultado === ResultTypeResult.MICROBIOLOGIA ? 'MICROBIOLOGÍA' : 'OTRO';

    return `${IdAnalito} - ${Analito} (${tipoTexto})`;
  };

  return (
    <Popup width={1200} height={'90%'} fullScreen={isMedium || isSmall || isXSmall} visible={visible} onHidden={handleVisible} title={getModalTitle()} showTitle={true} showCloseButton={true}>
      <Toolbar className='toolbar-common'>
        <Item widget='dxTabs' location='before'>
          <Tabs selectedIndex={Pestanas.findIndex((p) => p.id === selectedTab)} dataSource={Pestanas} width={'auto'} onItemClick={handleTabClick} scrollByContent showNavButtons={false} />
        </Item>
        {/* Indicador visual para tipos que soportan rangos extendidos */}
        {shouldShowRangosExtendidos && selectedTab === 1 && (
          <Item
            location='after'
            template={() => (
              <div
                style={{
                  background: '#e3f2fd',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#1976d2',
                  fontWeight: '500',
                }}>
                Rangos Extendidos Disponibles
              </div>
            )}
          />
        )}
      </Toolbar>

      {/* Renderiza las vistas de acuerdo con la pestaña seleccionada */}
      {selectedTab === 0 && (TipoResultado === ResultTypeResult.LISTA || TipoResultado === ResultTypeResult.AUTOCOMPLETAR) && <ResultListByAnalytes IdAnalito={IdAnalito} />}

      {selectedTab === 1 && (TipoResultado === ResultTypeResult.LISTA || TipoResultado === ResultTypeResult.AUTOCOMPLETAR) && <ReferencesText IdAnalito={IdAnalito} ValRefText={ValRefText} />}

      {selectedTab === 1 && (TipoResultado === ResultTypeResult.NUMERICO || TipoResultado === ResultTypeResult.FORMULA) && <ReferencesValues IdAnalito={IdAnalito} analitoNombre={Analito} />}

      {selectedTab === 1 && (TipoResultado === ResultTypeResult.TEXTUAL || TipoResultado === ResultTypeResult.MULTILINEA) && <ReferencesText IdAnalito={IdAnalito} ValRefText={ValRefText} />}

      {selectedTab === 3 && (TipoResultado === ResultTypeResult.PLANTILLA || TipoResultado === ResultTypeResult.IMAGENESDX) && (
        <TemplateConfigurationImproved
          IdAnalito={IdAnalito}
          configPlantillas={configPlantillas}
          onSave={(jsonData) => {
            setAnalitoTemplate({
              IdAnalito: IdAnalito,
              PlantillaJson: JSON.stringify(jsonData),
            })
              .then((result) => {})
              .catch((error) => {
                console.error('Error al guardar plantilla:', error);
              });
          }}
        />
      )}

      {/* NUEVA PESTAÑA PARA MICROBIOLOGÍA */}
      {selectedTab === 4 && TipoResultado === ResultTypeResult.MICROBIOLOGIA && <MicrobiologyConfiguration IdAnalito={IdAnalito} Analito={Analito} />}

      {selectedTab === 2 && <ValidationRules IdAnalito={IdAnalito} />}
    </Popup>
  );
};
